import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { Location } from '~commonInfra/utils/Location';
import { Set } from '~commonInfra/utils/Set';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { ToasterService } from '~src/commonInfra/services/ToasterService';
import { reloadSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { DeleteGroups, EditGroup, GroupAddUsers, GroupRemoveUsers, SyncAdGroup, SyncAdGroups, UpdateGroupsMinimumSiteRole, UpdateLocalGroupsLicenseUponLogin } from '../../bootstrap/redux/actions/AppActionTypes';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { GroupEditAction, GroupMenuAction, GroupMinimumSiteRoleUpdateAction, GroupsDeleteAction, GroupSyncAction, GroupUserAddAction, LocalGroupLicenseUponLoginUpdateAction } from '../groups/actions/GroupMenuActions';
import { IGroupInfo } from '../groups/models/IGroupInfo';
import { ActiveDirectoryGroupAction } from '../groups/services/actions/ActiveDirectoryGroupAction';
import { AddUsersToGroupAction } from '../groups/services/actions/AddUsersToGroupAction';
import { DeleteGroupAction } from '../groups/services/actions/DeleteGroupAction';
import { EditGroupDialogAction } from '../groups/services/actions/EditGroupDialogAction';
import { RemoveUsersFromGroupAction } from '../groups/services/actions/RemoveUsersFromGroupAction';
import { UpdateGroupsMinimumSiteRoleAction } from '../groups/services/actions/UpdateGroupsMinimumSiteRoleAction';
import { UpdateLocalGroupGrantLicenseModeAction } from '../groups/services/actions/UpdateLocalGroupGrantLicenseModeAction';
import { ISiteUserInfo } from '../users/models/ISiteUserInfo';

export function addUsers(group: IGroupInfo) {
  return AddUsersToGroupAction.showAddUsersToGroupDialog(group);
}

export function* handleAddUsers(action: GroupUserAddAction): SagaIterator {
  try {
    yield call(addUsers, action.group);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function removeUsers(users: ISiteUserInfo[], spaceInfo: SpaceInfo) {
  return RemoveUsersFromGroupAction.showRemoveUsersFromGroupDialog(Set.fromIdItemArray(users), spaceInfo.item as IGroupInfo);
}

export function* handleRemoveUsers(action: GroupMenuAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(removeUsers, action.items, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroup(spaceInfo: SpaceInfo) {
  const groupInfo = spaceInfo.item as IGroupInfo;
  return ActiveDirectoryGroupAction.showSyncAdGroupDialog(groupInfo);
}

export function* handleSyncGroup(action: GroupSyncAction): SagaIterator {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    yield call(syncGroup, spaceInfo);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function syncGroups(groups: IGroupInfo[]) {
  return ActiveDirectoryGroupAction.showSyncAdGroupsDialog(groups);
}

export function* handleSyncGroups(action: GroupSyncAction): SagaIterator {
  try {
    yield call(syncGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateMinSiteRole(groups: IGroupInfo[]) {
  return UpdateGroupsMinimumSiteRoleAction.showUpdateGroupsMinimumSiteRoleDialog(Set.fromIdItemArray(groups));
}

export function* handleMinSiteRoleUpdate(action: GroupMinimumSiteRoleUpdateAction): SagaIterator {
  try {
    yield call(updateMinSiteRole, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function updateLocalGroupGrantLicenseMode(groups: IGroupInfo[]) {
  return UpdateLocalGroupGrantLicenseModeAction
     .showUpdateGrantLicenseModeDialog(Set.fromIdItemArray(groups));
}

export function* handleLocalGroupGrantLicenseModeUpdate(action: LocalGroupLicenseUponLoginUpdateAction): SagaIterator {
  try {
    yield call(updateLocalGroupGrantLicenseMode, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function deleteGroups(groups: IGroupInfo[]) {
  return DeleteGroupAction.showDeleteGroupDialog(Set.fromIdItemArray(groups)).then(() => {
    ToasterService.instance.markToastsAsDoNotDismissOnLocationChange();
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    Location.instance.setUrl(siteUrls.groupsUrl());
  });
}

export function* handleDeleteGroups(action: GroupsDeleteAction): SagaIterator {
  try {
    yield call(deleteGroups, action.groups);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function editGroup(group: IGroupInfo) {
  return EditGroupDialogAction.showEditGroupDialog(group);
}

export function* handleEditGroup(action: GroupEditAction): SagaIterator {
  try {
    yield call(editGroup, action.group);
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootGroupMenuActionSaga(): SagaIterator {
  yield takeEvery(GroupAddUsers, handleAddUsers);
  yield takeEvery(GroupRemoveUsers, handleRemoveUsers);
  yield takeEvery(SyncAdGroup, handleSyncGroup);
  yield takeEvery(SyncAdGroups, handleSyncGroups);
  yield takeEvery(UpdateGroupsMinimumSiteRole, handleMinSiteRoleUpdate);
  yield takeEvery(UpdateLocalGroupsLicenseUponLogin, handleLocalGroupGrantLicenseModeUpdate);
  yield takeEvery(EditGroup, handleEditGroup);
  yield takeEvery(DeleteGroups, handleDeleteGroups);
}
